import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ensureLanguageLoaded } from "@/i18n/lazyLocales";

type LanguagePreference = "de" | "en" | "ja";

export function useLanguagePreference() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load language preference from database on mount
  useEffect(() => {
    const loadLanguagePreference = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("language_preference")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading language preference:", error);
          setIsLoading(false);
          return;
        }

        if (data?.language_preference) {
          // Locale-Chunk (en/ja) erst nachladen, dann umschalten — kein de-Flash (TES-1653).
          await ensureLanguageLoaded(data.language_preference);
          i18n.changeLanguage(data.language_preference);
        }
      } catch (error) {
        console.error("Error loading language preference:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguagePreference();
  }, [user, i18n]);

  // Save language preference to database
  const saveLanguagePreference = useCallback(
    async (newLanguage: LanguagePreference) => {
      await ensureLanguageLoaded(newLanguage);
      i18n.changeLanguage(newLanguage);

      if (!user) return;

      setIsSaving(true);
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ language_preference: newLanguage })
          .eq("user_id", user.id);

        if (error) {
          console.error("Error saving language preference:", error);
        }
      } catch (error) {
        console.error("Error saving language preference:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [user, i18n],
  );

  return {
    language: i18n.language as LanguagePreference,
    setLanguage: saveLanguagePreference,
    isLoading,
    isSaving,
  };
}
